import React, { useState } from 'react';
import { Zap, CheckCircle2, X, Loader2 } from 'lucide-react';
import { Switch } from '../../../../components/tailwind-component/ui/switch';
import { UPGRADE_BENEFITS, planLimitMessage, planLimitSubtitle, zapRowMeta, slotsUsedBy } from './upgradeBenefits';

/**
 * "You've used up your active automations" — shown BEFORE the wizard opens.
 *
 * It used to appear at the posts step, after the creator had already chosen a
 * goal, answered the follow question and started picking posts; the popup's
 * close handler then threw all of that away. Catching it on the click that
 * would open the wizard means nothing is lost.
 *
 * It also offers the two ways out that cost nothing — pause a running Zap right
 * here, or go and look at them in My Zaps — instead of only Upgrade. Freeing a
 * slot turns the CTA into "Continue", which opens the wizard they were heading
 * for.
 *
 * Mobile counterpart: PlanLimitModal.tsx in clanconnect_mobile.
 */

const GRADIENT = 'linear-gradient(135deg, #833ab4 0%, #e1306c 50%, #fcaf45 100%)';

export interface PlanLimitAutomation {
  ig_automation_id: number;
  automation_name?: string;
  name?: string;
  is_active?: boolean | number;
  content_type?: string;
  /** The posts this Zap watches — its ACTIVE ones are what count against the plan. */
  automation_posts_data?: { is_active?: boolean }[];
}

/**
 * What this Zap holds against the plan. Its active posts — except for a Live
 * Zap, which has none attached yet still occupies a slot, so slotsUsedBy()
 * counts it as one and the rows keep adding up to the headline total.
 */
export const activePostCount = (a: PlanLimitAutomation): number =>
  slotsUsedBy((a.automation_posts_data || []).filter((p) => !!p?.is_active).length, a.content_type);

interface Props {
  open: boolean;
  used: number;
  limit: any;
  automations: PlanLimitAutomation[];
  currentPlanName?: string | null;
  onTogglePause: (ig_automation_id: number, nextActive: boolean) => Promise<any>;
  onGoToMyZaps: () => void;
  onContinue: () => void;
  onUpgrade: () => void;
  onClose: () => void;
}

export const PlanLimitModal: React.FC<Props> = ({
  open,
  used,
  limit,
  automations,
  currentPlanName,
  onTogglePause,
  onGoToMyZaps,
  onContinue,
  onUpgrade,
  onClose,
}) => {
  const [busyId, setBusyId] = useState<number | null>(null);
  if (!open) return null;

  const isSavingsPlan = currentPlanName === 'Savings';
  const active = automations.filter((a) => !!a.is_active);
  const message = planLimitMessage(used, limit, active.length);
  // Coerced, because a limit arriving as a string used to fall through to
  // `true` and show "You have room again" to someone who had none.
  const cap = typeof limit === 'number' ? limit : Number(limit);
  const hasRoom = !Number.isFinite(cap) || cap < 0 || used < cap;

  const handleToggle = async (a: PlanLimitAutomation) => {
    setBusyId(a.ig_automation_id);
    try {
      await onTogglePause(a.ig_automation_id, false);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/55 px-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-5 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div
          className="mx-auto mt-1 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ background: GRADIENT }}
        >
          <Zap className="h-7 w-7 text-white" fill="#fff" />
        </div>

        <h3 className="mt-3 mb-1 text-lg font-bold text-gray-900">
          {hasRoom ? 'You have room again' : 'All your Zaps are in use'}
        </h3>
        <p className="mb-4 text-sm text-gray-500">
          {hasRoom ? 'Carry on and build your new Zap.' : planLimitSubtitle(used, limit)}
        </p>

        {/* OLD: "Pause one below to free up a slot" unconditionally — wrong for
            anyone who built several Zaps on a paid plan and has since lapsed to
            a smaller one, where pausing one still leaves them over the cap. */}
        {!hasRoom && !!message && (
          <div className="mb-3 w-full rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-800">
            {message}
          </div>
        )}

        {active.length > 0 && (
          <div className="mb-3 w-full space-y-3 rounded-xl bg-gray-50 p-4 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              Your running Zaps
            </p>
            {active.map((a) => {
              const label = a.automation_name || a.name || `Automation ${a.ig_automation_id}`;
              const busy = busyId === a.ig_automation_id;
              return (
                <div key={a.ig_automation_id} className="flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="mb-0 truncate text-sm font-semibold text-gray-900">{label}</p>
                    {/* OLD: just "Running", which hid the fact that one Zap can
                        hold several posts — the whole reason "3 of 1" looked
                        wrong next to two rows. */}
                    <p className="mb-0 text-xs text-gray-500">
                      {zapRowMeta(activePostCount(a), a.content_type)}
                    </p>
                  </div>
                  {busy ? (
                    <Loader2 className="h-4 w-4 animate-spin text-[#7c3aed]" />
                  ) : (
                    <Switch checked onCheckedChange={() => handleToggle(a)} />
                  )}
                </div>
              );
            })}
            <p className="mb-0 text-xs text-gray-400">
              Nothing is deleted — it just stops replying until you switch it back on.
            </p>
          </div>
        )}

        {!hasRoom && (
          <div className="mb-4 w-full space-y-3 rounded-xl bg-gray-50 p-4 text-left">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              What you unlock with upgrade
            </p>
            {UPGRADE_BENEFITS.map((benefit) => (
              <div key={benefit} className="flex items-center gap-3 text-sm text-gray-700">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-green-500" />
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        )}

        {hasRoom ? (
          <button
            type="button"
            onClick={onContinue}
            className="w-full rounded-xl bg-black py-3 text-sm font-semibold text-white transition hover:opacity-90"
          >
            Continue
          </button>
        ) : (
          !isSavingsPlan && (
            <button
              type="button"
              onClick={onUpgrade}
              className="w-full rounded-xl py-3 text-sm font-semibold text-white transition hover:opacity-90"
              style={{ background: GRADIENT }}
            >
              Upgrade Now
            </button>
          )
        )}

        <button
          type="button"
          onClick={onGoToMyZaps}
          className="mt-3 text-sm font-semibold text-[#7c3aed] hover:underline"
        >
          Go to My Zaps
        </button>
      </div>
    </div>
  );
};

export default PlanLimitModal;
